// BOXX operator console — application shell.
//
// This file owns four things and nothing else: the session, the router, the
// navigation rail, and the permission gate. Every screen lives in a sibling
// module and is mounted here.
//
// There is no router package. The route table is a flat list of patterns with a
// tiny matcher, which is all the static routes and the one parameterised route
// need. Adding react-router would buy nothing we are not doing in 60 lines and
// would put a second source of truth beside the permission table.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ApiError, connectSocket, http, onAuthLost, org as orgStore } from "./api";
import { readMode, type Mode } from "./theme";
import { Logo } from "./logo";
import { ToastHost } from "./ui";

import { LoginScreen, SSOCallbackScreen } from "./auth";
import { DashboardView } from "./dashboard";
import { ChatbotSettingsView, ChatbotView, TestConsoleView } from "./chatbot";
import { KnowledgeCentreView, KnowledgeSourceDetailView } from "./knowledge";
import { ScamKgView } from "./fraudgraph";
import { EvalOpsView, ModelLabView } from "./evalops";
import {
  ChatbotAnalyticsView,
  MetaInsightsView,
  NPSFeedbackView,
  ObservabilityView,
  OpsRecoveryView,
  QualityView,
  TesterActivityView,
  UsageView,
} from "./analytics";
import {
  AccountsView,
  ContactsView,
  DocumentsView,
  FlowsView,
  ProfileView,
  SettingsView,
  TagsView,
  TemplatesView,
} from "./settings";
import {
  APIKeysView,
  AuditLogsView,
  CustomActionsView,
  RolesView,
  SSOSettingsView,
  TeamsView,
  UsersView,
  WebhooksView,
} from "./settings_admin";

// ============================================================ session

export interface Me {
  id: string;
  email: string;
  full_name: string;
  organization_id: string;
  is_super_admin: boolean;
  is_available: boolean;
  role?: { id: string; name: string } | null;
  permissions?: Record<string, string[]>;
  settings?: Record<string, unknown>;
}

type Action = "read" | "write" | "delete";

interface Session {
  me: Me | null;
  loading: boolean;
  /** Resource + action check. Super admins bypass; unknown resource means no. */
  can: (resource: string, action?: Action) => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<Session>({
  me: null,
  loading: true,
  can: () => false,
  refresh: async () => {},
  logout: async () => {},
});

export const useSession = () => useContext(SessionContext);

// ============================================================ routes

interface Route {
  /** ":id" matches one non-empty segment. */
  pattern: string;
  title: string;
  /** Resource permission required to see this route at all. A list means ANY one of them. */
  permission?: string | string[];
  render: (params: Record<string, string>) => JSX.Element;
}

const ROUTES: Route[] = [
  { pattern: "/", title: "Dashboard", permission: "analytics", render: () => <DashboardView /> },

  { pattern: "/chatbot", title: "Chatbot", permission: "settings.chatbot", render: () => <ChatbotView /> },
  { pattern: "/chatbot/settings", title: "Chatbot settings", permission: "settings.chatbot", render: () => <ChatbotSettingsView /> },
  { pattern: "/chatbot/scam-kg", title: "Scam knowledge graph", permission: "settings.chatbot", render: () => <ScamKgView /> },
  { pattern: "/chatbot/knowledge-centre", title: "Knowledge Centre", permission: "settings.chatbot", render: () => <KnowledgeCentreView /> },
  { pattern: "/chatbot/knowledge-centre/:id", title: "Knowledge source", permission: "settings.chatbot", render: (p) => <KnowledgeSourceDetailView sourceId={p.id} /> },
  { pattern: "/chatbot/model-lab", title: "Model Lab", permission: "settings.chatbot", render: () => <ModelLabView /> },
  { pattern: "/chatbot/test-console", title: "Test console", permission: ["test_console", "settings.chatbot"], render: () => <TestConsoleView /> },
  { pattern: "/chatbot/eval-ops", title: "Eval ops", permission: "settings.chatbot", render: () => <EvalOpsView /> },

  { pattern: "/analytics/chatbot", title: "Chatbot analytics", permission: "analytics", render: () => <ChatbotAnalyticsView /> },
  { pattern: "/analytics/nps", title: "NPS feedback", permission: "analytics", render: () => <NPSFeedbackView /> },
  { pattern: "/analytics/meta", title: "Meta insights", permission: "analytics", render: () => <MetaInsightsView /> },
  { pattern: "/analytics/usage", title: "Usage", permission: "analytics", render: () => <UsageView /> },
  { pattern: "/analytics/testers", title: "Tester activity", permission: "users", render: () => <TesterActivityView /> },
  { pattern: "/analytics/observability", title: "Observability", permission: "analytics", render: () => <ObservabilityView /> },
  { pattern: "/analytics/quality", title: "Quality", permission: "analytics", render: () => <QualityView /> },
  { pattern: "/analytics/ops-recovery", title: "Ops recovery", permission: "analytics", render: () => <OpsRecoveryView /> },

  { pattern: "/templates", title: "Templates", permission: "templates", render: () => <TemplatesView /> },
  { pattern: "/documents", title: "Documents", permission: "documents", render: () => <DocumentsView /> },
  { pattern: "/profile", title: "Profile", render: () => <ProfileView /> },

  { pattern: "/settings", title: "Settings", permission: "settings.general", render: () => <SettingsView /> },
  { pattern: "/settings/accounts", title: "WhatsApp accounts", permission: "accounts", render: () => <AccountsView /> },
  { pattern: "/settings/api-keys", title: "API keys", permission: "settings.general", render: () => <APIKeysView /> },
  { pattern: "/settings/audit-logs", title: "Audit logs", permission: "settings.general", render: () => <AuditLogsView /> },
  { pattern: "/settings/contacts", title: "Contacts", permission: "contacts", render: () => <ContactsView /> },
  { pattern: "/settings/custom-actions", title: "Custom actions", permission: "settings.general", render: () => <CustomActionsView /> },
  { pattern: "/settings/flows", title: "WhatsApp flows", permission: "settings.general", render: () => <FlowsView /> },
  { pattern: "/settings/roles", title: "Roles", permission: "roles", render: () => <RolesView /> },
  { pattern: "/settings/sso", title: "Single sign-on", permission: "settings.general", render: () => <SSOSettingsView /> },
  { pattern: "/settings/tags", title: "Tags", permission: "tags", render: () => <TagsView /> },
  { pattern: "/settings/teams", title: "Teams", permission: "teams", render: () => <TeamsView /> },
  { pattern: "/settings/users", title: "Users", permission: "users", render: () => <UsersView /> },
  { pattern: "/settings/webhooks", title: "Webhooks", permission: "settings.general", render: () => <WebhooksView /> },
];

function matchRoute(path: string): { route: Route; params: Record<string, string> } | null {
  const parts = path.replace(/\/+$/, "").split("/").filter(Boolean);
  for (const route of ROUTES) {
    const pat = route.pattern.split("/").filter(Boolean);
    if (pat.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pat.length; i++) {
      if (pat[i].startsWith(":")) params[pat[i].slice(1)] = decodeURIComponent(parts[i]);
      else if (pat[i] !== parts[i]) { ok = false; break; }
    }
    if (ok) return { route, params };
  }
  return null;
}

// ============================================================ navigation

interface NavEntry {
  path: string;
  label: string;
  permission?: string | string[];
  icon: string;
}
interface NavSection {
  label?: string;
  items: NavEntry[];
}

// Order is the operator's mental model of the product: what is happening now,
// then what answers it, then what proves it works, then configuration.
const NAV: NavSection[] = [
  {
    items: [{ path: "/", label: "Dashboard", permission: "analytics", icon: "▦" }],
  },
  {
    label: "Concierge",
    items: [
      { path: "/chatbot", label: "Chatbot", permission: "settings.chatbot", icon: "✦" },
      { path: "/chatbot/knowledge-centre", label: "Knowledge Centre", permission: "settings.chatbot", icon: "▤" },
      { path: "/chatbot/scam-kg", label: "Scam KG", permission: "settings.chatbot", icon: "⊛" },
    ],
  },
  {
    label: "Evidence",
    items: [
      { path: "/chatbot/test-console", label: "Test console", permission: ["test_console", "settings.chatbot"], icon: "▷" },
      { path: "/chatbot/model-lab", label: "Model Lab", permission: "settings.chatbot", icon: "⚗" },
      { path: "/chatbot/eval-ops", label: "Eval ops", permission: "settings.chatbot", icon: "◎" },
      { path: "/analytics/observability", label: "Observability", permission: "analytics", icon: "◐" },
      { path: "/analytics/quality", label: "Quality", permission: "analytics", icon: "✓" },
      { path: "/analytics/ops-recovery", label: "Ops recovery", permission: "analytics", icon: "⟲" },
    ],
  },
  {
    label: "Outbound",
    items: [
      { path: "/templates", label: "Templates", permission: "templates", icon: "▣" },
      { path: "/documents", label: "Documents", permission: "documents", icon: "▥" },
    ],
  },
  {
    label: "Analytics",
    items: [
      { path: "/analytics/chatbot", label: "Chatbot", permission: "analytics", icon: "◕" },
      { path: "/analytics/nps", label: "NPS", permission: "analytics", icon: "☺" },
      { path: "/analytics/usage", label: "Usage", permission: "analytics", icon: "∑" },
      { path: "/analytics/testers", label: "Tester activity", permission: "users", icon: "◔" },
    ],
  },
  {
    items: [{ path: "/settings", label: "Settings", permission: "settings.general", icon: "⚙" }],
  },
];

// ============================================================ router hook

function usePath(): [string, (to: string, replace?: boolean) => void] {
  const [path, setPath] = useState(() => window.location.pathname || "/");

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname || "/");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string, replace = false) => {
    if (to === window.location.pathname) return;
    window.history[replace ? "replaceState" : "pushState"]({}, "", to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  return [path, navigate];
}

/** Intercept same-origin left-clicks on <a href> so links do not full-reload. */
function useLinkInterception(navigate: (to: string) => void) {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/") || anchor.getAttribute("target")) return;
      e.preventDefault();
      navigate(href);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
}

// ============================================================ theme

function useTheme(): [Mode, () => void] {
  const [mode, setMode] = useState<Mode>(() => {
    const saved = localStorage.getItem("theme");
    return saved === "light" || saved === "dark" ? saved : readMode();
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    localStorage.setItem("theme", mode);
  }, [mode]);
  return [mode, () => setMode((m) => (m === "dark" ? "light" : "dark"))];
}

// ============================================================ shell

export default function App() {
  const [path, navigate] = usePath();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, toggleTheme] = useTheme();
  const [live, setLive] = useState(false);
  const [railOpen, setRailOpen] = useState(false);

  useLinkInterception(navigate);

  const loadMe = useCallback(async () => {
    try {
      setMe(await http.get<Me>("/me"));
    } catch (err) {
      // 401 here is the normal signed-out case, not a failure worth surfacing.
      if (!(err instanceof ApiError) || !err.isAuth) console.error(err);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMe();
    return onAuthLost(() => setMe(null));
  }, [loadMe]);

  // The live socket exists only while signed in.
  useEffect(() => {
    if (!me) return;
    return connectSocket(
      () => {
        // Individual screens subscribe to the events they care about; the shell
        // only tracks whether the socket is up.
      },
      (up) => setLive(up)
    );
  }, [me]);

  const can = useCallback(
    (resource: string, action: Action = "read") => {
      if (!me) return false;
      if (me.is_super_admin) return true;
      return Boolean(me.permissions?.[resource]?.includes(action));
    },
    [me]
  );

  /** A route or nav permission: one resource, or ANY of a list. See the `tester` role. */
  const allowed = useCallback(
    (permission?: string | string[]) =>
      !permission || (Array.isArray(permission) ? permission.some((r) => can(r)) : can(permission)),
    [can]
  );

  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } finally {
      orgStore.select(null);
      setMe(null);
      navigate("/", true);
    }
  }, [navigate]);

  const session = useMemo<Session>(
    () => ({ me, loading, can, refresh: loadMe, logout }),
    [me, loading, can, loadMe, logout]
  );

  // ---- unauthenticated ----------------------------------------------------

  if (path === "/auth/sso/callback") {
    return <SSOCallbackScreen onAuthenticated={loadMe} navigate={navigate} />;
  }

  if (loading) {
    return (
      <div className="auth-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!me) {
    return <LoginScreen onAuthenticated={loadMe} navigate={navigate} />;
  }

  // ---- authenticated ------------------------------------------------------

  // Land on the first screen this operator can actually open, rather than
  // showing an empty dashboard to someone with no analytics permission.
  const firstAllowed =
    NAV.flatMap((s) => s.items).find((i) => allowed(i.permission))?.path ?? "/profile";

  // HOME IS WHEREVER YOU ARE ALLOWED TO START. A tester holds the Test Console and nothing else, so
  // "/" - the dashboard - is the one screen guaranteed to say "No access" to them, and it is the
  // screen every login and the logo link lands on. They are taken to their first screen instead.
  const home = matchRoute("/");
  const redirectHome = path === "/" && firstAllowed !== "/" && !!home && !allowed(home.route.permission);
  if (redirectHome) queueMicrotask(() => navigate(firstAllowed, true));
  const matched = matchRoute(redirectHome ? firstAllowed : path);

  let content: JSX.Element;
  if (!matched) {
    content = (
      <div className="page">
        <div className="empty">
          <h3>Page not found</h3>
          <p>
            Nothing lives at <code>{path}</code>.
          </p>
          <a className="btn" href={firstAllowed}>
            Go back
          </a>
        </div>
      </div>
    );
  } else if (!allowed(matched.route.permission)) {
    content = (
      <div className="page">
        <div className="empty">
          <h3>No access</h3>
          <p>
            Your role does not include{" "}
            <code>{[matched.route.permission].flat().join(" or ")}</code>. Ask an
            administrator if you need it.
          </p>
          <a className="btn" href={firstAllowed}>
            Go to {firstAllowed}
          </a>
        </div>
      </div>
    );
  } else {
    content = matched.route.render(matched.params);
  }

  const title = matched?.route.title ?? "Not found";
  document.title = `${title} · BOXX`;

  return (
    <SessionContext.Provider value={session}>
      <div className={`app${railOpen ? " rail-open" : ""}`}>
        <aside className="rail">
          <a className="brand" href="/" aria-label="BOXX — home">
            <Logo />
          </a>

          <nav className="nav">
            {NAV.map((section, si) => {
              const items = section.items.filter((i) => allowed(i.permission));
              if (!items.length) return null;
              return (
                <div className={si === 0 ? "" : "nav-group"} key={section.label ?? si}>
                  {section.label && <div className="nav-group-label">{section.label}</div>}
                  {items.map((item) => (
                    <a
                      key={item.path}
                      href={item.path}
                      className={`nav-item${isActive(path, item.path) ? " active" : ""}`}
                      onClick={() => setRailOpen(false)}
                    >
                      <span className="icon" aria-hidden>
                        {item.icon}
                      </span>
                      {item.label}
                    </a>
                  ))}
                </div>
              );
            })}
          </nav>

          <div className="rail-footer">
            <a href="/profile" className={`nav-item${path === "/profile" ? " active" : ""}`}>
              <span className="icon" aria-hidden>
                ◍
              </span>
              <span className="truncate">{me.full_name || me.email}</span>
            </a>
            <button className="nav-item" onClick={toggleTheme}>
              <span className="icon" aria-hidden>
                {mode === "dark" ? "☾" : "☀"}
              </span>
              {mode === "dark" ? "Dark" : "Light"}
            </button>
            <button className="nav-item" onClick={logout}>
              <span className="icon" aria-hidden>
                ⏻
              </span>
              Sign out
            </button>
          </div>
        </aside>

        <div className="main">
          <header className="header">
            <button
              className="btn icon ghost"
              onClick={() => setRailOpen((v) => !v)}
              aria-label="Toggle navigation"
            >
              ☰
            </button>
            <span className="title">{title}</span>
            <span className="spacer" />
            <span
              className={`badge ${live ? "good" : "warning"}`}
              title={live ? "Live updates connected" : "Reconnecting to live updates"}
            >
              <span className="dot" />
              {live ? "Live" : "Offline"}
            </span>
          </header>
          {content}
        </div>

        <ToastHost />
      </div>
    </SessionContext.Provider>
  );
}

/** A nav row is active on exact match, or when the current path is beneath it. */
function isActive(current: string, target: string): boolean {
  if (target === "/") return current === "/";
  return current === target || current.startsWith(target + "/");
}
